import { Column, ColumnOptions as GrecoColumnOptions } from "@greco/components";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useGetTaxonomies } from "state/queries/useGetTaxonomies";
import { Taxonomy } from "library/types";
import { ExpandedColumn } from "library/types/columns";
import {
  useColumnOptionsSettingsValue,
  useCreateSetting,
  useDeleteSetting,
  useLoadSettings,
  useSelectedViewColumnsValue,
  useSelectedViewValue,
  useSetColumnsValue,
  useSetSelectedViewIdValue,
  useSetTempColumnsValue,
  useTempColumnsValue,
  useUpdateSetting,
} from "store/offersContext";
import { defaultCols } from "../columns";
import { Setting } from "library/types/types_old";
import { TFunction } from "i18next";
import { APP_ID } from "library/constants";

const reorderColumn = (
  columns: ExpandedColumn[] | undefined,
  index1: number,
  index2: number
) => {
  if (columns && columns.length > 0) {
    const clone = [...columns];
    const [movedItem] = clone.splice(index1, 1);
    if (movedItem) {
      clone.splice(index2, 0, movedItem);
    }
    return clone;
  }
};

export const ColumnOptions = ({
  isOpen,
  closePanel,
}: {
  isOpen: boolean;
  closePanel: () => void;
}) => {
  const { data: taxonomies, isFetching } = useGetTaxonomies();
  const { t } = useTranslation();

  // Transform taxonomies to { [key: string]: Taxonomy[] } with numeric ids
  const transformedTaxonomies = useMemo(() => {
    if (!taxonomies) return {};
    return Object.entries(taxonomies).reduce((acc, [key, value]) => {
      acc[key] = Array.isArray(value)
        ? value.map((item) => ({
            ...item,
            id:
              typeof item.id === "string"
                ? parseInt(item.id, 10) || 0
                : item.id,
          }))
        : [];
      return acc;
    }, {} as { [key: string]: any[] });
  }, [taxonomies]);

  const selectedViewColumnsValue = useSelectedViewColumnsValue();
  const setSelectedViewIdValue = useSetSelectedViewIdValue();
  const selectedViewValue = useSelectedViewValue();
  const tempColumnsValue = useTempColumnsValue();
  const setTempColumnsValue = useSetTempColumnsValue();
  const loadStatusValue = useLoadSettings();

  const columnOptionsSettingsValue = useColumnOptionsSettingsValue();
  const createSetting = useCreateSetting();
  const deleteSetting = useDeleteSetting();
  const updateSetting = useUpdateSetting();
  const setColumnsValue = useSetColumnsValue();

  const areSettingsLoading = loadStatusValue === "loading";
  const isCreateSettingLoading = loadStatusValue === "loading";
  const isDeleteSettingLoading = loadStatusValue === "loading";
  const isUpdateSettingLoading = loadStatusValue === "loading";
  const tempColumns = tempColumnsValue;
  const areSettingsLoaded =
    loadStatusValue === "success" || loadStatusValue === "none";
  const selectedView = selectedViewValue;
  const selectedViewColumns = selectedViewColumnsValue;
  const columnOptionsSettings = columnOptionsSettingsValue;

  const isViewLoading =
    areSettingsLoading ||
    isCreateSettingLoading ||
    isDeleteSettingLoading ||
    isUpdateSettingLoading;

  const transformedColumns = useMemo(() => {
    return tempColumns?.map((c) => {
      if (c.key === "options" || c.key === "SelectedInsurer") {
        return {
          ...c,
          label: t(c.labelKey),
          isPresentInColumnOptions: false,
        };
      }
      return {
        ...c,
        label: t(c.labelKey),
      };
    });
  }, [tempColumns]);
  if (!isOpen) return null;

  return (
    <div>
      <GrecoColumnOptions
        t={t as any}
        columns={transformedColumns as Column[]}
        isReady={areSettingsLoaded}
        isViewLoading={isViewLoading}
        closePanel={closePanel}
        onChangeView={(id) => {
          setSelectedViewIdValue(id);
        }}
        onClickApply={() => {
          setColumnsValue(tempColumns);
          closePanel();
        }}
        onClickClearAllFilters={() => {
          setTempColumnsValue(
            tempColumns.map((c) => ({
              ...c,
              filter: defaultCols.find((dc) => dc.key === c.key)?.filter,
            }))
          );
        }}
        onClickDeleteView={() => {
          deleteSetting(selectedView.userAppSettingId);
        }}
        onClickReset={() => {
          setTempColumnsValue(selectedViewColumns);
        }}
        onClickSaveView={() => {
          updateSetting({
            ...selectedView,
            userAppSettingValue: JSON.stringify(tempColumns),
          });
        }}
        onCreateView={(data) => {
          const newSetting = {
            applicationCodeId: APP_ID,
            appSettingCodeId: 900000000010303,
            userAppSettingValue: JSON.stringify(tempColumns),
            ...data,
          } as Setting;
          createSetting(newSetting);
          closePanel();
        }}
        onUpdateView={(data) => {
          updateSetting({
            ...selectedView,
            ...data,
          });
          closePanel();
        }}
        onDeselectAll={() => {
          setTempColumnsValue(
            tempColumns.map((c) => ({
              ...c,
              isVisible: c.isFixed ? true : false,
            }))
          );
        }}
        onSelectAll={() => {
          setTempColumnsValue(
            tempColumns.map((c) => ({
              ...c,
              isVisible: true,
            }))
          );
        }}
        onReorderColumn={(src, dest) => {
          setTempColumnsValue(reorderColumn(tempColumns, src, dest));
        }}
        onToggleColumnVisibility={(columnKey) => {
          setTempColumnsValue(
            tempColumns.map((c) => {
              if (c.key === columnKey) {
                return {
                  ...c,
                  isVisible: !c.isVisible,
                };
              }
              return c;
            })
          );
        }}
        selectedView={selectedView}
        setFilter={(columnKey, filter) => {
          setTempColumnsValue(
            tempColumns.map((c) => {
              if (c.key === columnKey) {
                return {
                  ...c,
                  filter,
                };
              }
              return c;
            })
          );
        }}
        taxonomy={transformedTaxonomies}
        views={columnOptionsSettings}
      />
    </div>
  );
};
